import { EventEmitter, Injectable, Output } from '@angular/core';
import dayjs from 'dayjs';
import { Socket } from 'ngx-socket-io';

@Injectable({
  providedIn: 'root'
})

export class ChatService {

  @Output() customerFacingDataLength: EventEmitter<number> = new EventEmitter<number>()
  @Output() frohokRoomSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() backofficeUnreadMessagesChanged: EventEmitter<number> = new EventEmitter<number>();
  private customerFacingMessageCount = 0;
  private customerSocketListenersRegistered = false;
  private backofficeUnreadCount = 0;
  private backofficeSocketListenersRegistered = false;

  constructor(private socket: Socket) {
    this.registerCustomerFacingSocketListeners();
  }

  /**
   * Publikusan jelzi a FROhok topbar fele, hogy egy szoba aktiv lett, es mennyit kell azonnal levonni.
   */
  emitFrohokRoomSelected(room: any): void {
    this.frohokRoomSelected.emit(room);
  }

  /**
   * Kezzel felulirja a customer-facing helyi uzenetszamlalot, majd broadcastolja az uj allapotot.
   */
  sendChatDataToCustomerFacing(chatLenght: number) {
    this.customerFacingMessageCount = Math.max(0, Number(chatLenght) || 0);
    this.customerFacingDataLength.emit(this.customerFacingMessageCount)
  }

  /**
   * A customer-facing badge baseline-t (utolso olvasott mennyiseg) explicit modon szinkronizalja.
   */
  syncCustomerFacingCounterFromCurrentChat(chatLength: number): void {
    this.sendChatDataToCustomerFacing(chatLength);
  }

  /**
   * A customer-facing socket esemenyekbol folyamatosan frissiti a teljes uzenetszamot,
   * igy minimalizalt allapotban is mukodik a badge szamlalo.
   */
  private registerCustomerFacingSocketListeners(): void {
    if (this.customerSocketListenersRegistered) {
      return;
    }

    this.customerSocketListenersRegistered = true;

    this.socket.on('chatData', (messages: unknown) => {
      if (!this.isCustomerFacingRoomAvailable()) {
        return;
      }

      if (!Array.isArray(messages)) {
        this.sendChatDataToCustomerFacing(0);
        return;
      }

      this.sendChatDataToCustomerFacing(messages.length);
    });

    this.socket.on('chatMessageDelta', (payload: { room_uuid?: string } | null | undefined) => {
      const guestUuid = this.getGuestRoomUuid();
      if (!guestUuid || !payload?.room_uuid || payload.room_uuid !== guestUuid) {
        return;
      }

      this.sendChatDataToCustomerFacing(this.customerFacingMessageCount + 1);
    });
  }

  /**
   * Visszaadja, hogy van-e aktiv customer-facing szobaazonosito a session-ben.
   */
  private isCustomerFacingRoomAvailable(): boolean {
    return !!this.getGuestRoomUuid();
  }

  /**
   * Centralizalt helyrol olvassa ki a customer-facing szoba UUID-t.
   */
  private getGuestRoomUuid(): string {
    const localGuestUuid = localStorage.getItem('guestUuid');
    if (localGuestUuid) {
      return localGuestUuid;
    }

    const sessionGuestUuid = sessionStorage.getItem('guestUuid');
    if (sessionGuestUuid) {
      localStorage.setItem('guestUuid', sessionGuestUuid);
      return sessionGuestUuid;
    }

    return '';
  }

  /**
   * Backoffice oldalrol inicializalja az unread szobaszam figyelest.
   * A sidebar ennek alapjan tudja kirajzolni a notification jelzest.
   */
  initBackofficeUnreadTracking(): void {
    const backofficeContext = this.getBackofficeContext();
    if (!backofficeContext) {
      this.publishBackofficeUnreadCount(0);
      return;
    }

    if (!this.backofficeSocketListenersRegistered) {
      this.backofficeSocketListenersRegistered = true;

      this.socket.on('selectedRoomsByHotel', (rooms: unknown) => {
        this.publishBackofficeUnreadCount(this.calculateUnreadCountFromRooms(rooms));
      });

      this.socket.on('roomDeleted', () => {
        this.requestBackofficeRooms(backofficeContext);
      });
    }

    this.requestBackofficeRooms(backofficeContext);
  }

  /**
   * Visszaadja az utoljara ismert backoffice unread szamlalot.
   */
  getBackofficeUnreadCount(): number {
    return this.backofficeUnreadCount;
  }

  /**
   * SessionStorage-bol biztonsagosan kiolvassa a backoffice inicializalashoz szukseges user adatokat.
   */
  private getBackofficeContext(): { name: string; subscriber: string; hotel_id: number } | null {
    const rawUserInfo = sessionStorage.getItem('userInfo');
    if (!rawUserInfo) {
      return null;
    }

    try {
      const userInfo = JSON.parse(rawUserInfo);
      const name = String(userInfo?.Name || '').trim();
      const subscriber = String(userInfo?.SubscriberTree?.SubscriberCode || '').trim();
      const hotelId = Number(userInfo?.SubscriberTree?.Htlnum);

      if (!name || !subscriber || Number.isNaN(hotelId)) {
        return null;
      }

      return {
        name,
        subscriber,
        hotel_id: hotelId,
      };
    } catch {
      return null;
    }
  }

  /**
   * Backoffice szobainicializalo payload kuldese a szerver fele.
   */
  private requestBackofficeRooms(context: { name: string; subscriber: string; hotel_id: number }): void {
    this.socket.emit('receptionist_init', {
      name: context.name,
      subscriber: context.subscriber,
      hotel_id: context.hotel_id,
    });
  }

  /**
   * A szerver altal kuldott szobalistabol osszegzi az olvasatlan uzenetszamot.
   */
  private calculateUnreadCountFromRooms(rooms: unknown): number {
    if (!Array.isArray(rooms)) {
      return 0;
    }

    return rooms.reduce((sum: number, room: unknown) => {
      const messageCounter = Number((room as { message_counter?: number | string })?.message_counter) || 0;
      return sum + Math.max(messageCounter, 0);
    }, 0);
  }

  /**
   * Emiteli az unread szamlalot csak valtozas eseten.
   */
  private publishBackofficeUnreadCount(count: number): void {
    const normalizedCount = Math.max(0, Number(count) || 0);
    if (normalizedCount === this.backofficeUnreadCount) {
      return;
    }

    this.backofficeUnreadCount = normalizedCount;
    this.backofficeUnreadMessagesChanged.emit(this.backofficeUnreadCount);
  }

  isWithin(): boolean {
    const activeHours = sessionStorage.getItem('cah');
    if (!activeHours) {
      return true;
    }

    const activeHoursArr = activeHours.split(',');
    if (activeHoursArr.length < 2) {
      return true;
    }

    const startParts = activeHoursArr[0].split(':');
    const endParts = activeHoursArr[1].split(':');

    if (startParts.length < 2 || endParts.length < 2) {
      return true;
    }

    const startHour = Number(startParts[0]);
    const startMinute = Number(startParts[1]);
    const endHour = Number(endParts[0]);
    const endMinute = Number(endParts[1]);

    if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) {
      return true;
    }

    const start = dayjs().hour(startHour).minute(startMinute).second(0);
    const end = dayjs().hour(endHour).minute(endMinute).second(0);
    const now = dayjs();

    return now.isAfter(start) && now.isBefore(end);
  }

}
